import { Injectable } from "@angular/core";
import { RemoteGatewayFactory } from "../../gateways/remote-gateway-factory";
import { SearchCompanyParser } from "../parsers/pbm/company/search-company.parser";
import { Company } from "../../models/domains/pbm/companies/company";
import { BackendUrl } from "../../models/domains/urls/back-end.url";
import { PageableResult } from "../../models/domains/paginator/pageable-result";
import { PagingRequest } from "../../models/domains/paginator/paging-request";
import { CompanyParser } from "../parsers/pbm/company/company.parser";
import { AddCompanyParser } from "../parsers/pbm/company/add-company.parser";
import { CompanyContact } from "../../models/domains/pbm/companies/company-contact";
import { ContactParser } from "../parsers/pbm/company/contact.parser";
import { AddContactParser } from "../parsers/pbm/company/add-contact.parser";
import { CompanyMasterData } from "../../models/domains/pbm/companies/company-master-data";
import { SearchCompanyFromMDMParser } from "../parsers/pbm/company/search-company-from-mdm.parser";
import { ExportOrderKey } from "../../models/domains/pbm/exports/export-order-key";
import { UserRoleMediator } from "../mediators/user-role.mediator";
import { CompanyFinancialReportsPageableResult } from "../../models/domains/pbm/companies/company-financial-reports-pageable-result.model";
import { CompanyFinancialReportsParser } from "../parsers/pbm/company/company-financial-reports.parser";
import { FiscalNumber } from "../../models/domains/pbm/fiscal-number";
import { ICompanyFilterParams } from "../../models/domains/pbm/companies/company-filter-params";
import { CompanyConfiguration } from "../../models/domains/pbm/companies/company-configuration";
import { CompanyConfigurationParser } from "../parsers/pbm/company/company-configuration.parser";
import { CompanyRemainingLimit } from "../../models/domains/pbm/companies/company-remaining-limit";
import { XLSXFileExportResult } from "../../models/domains/pbm/exports/file-export-result";
import { ExportFileAsXLSXParser } from "../parsers/export-file-as-xlsx.parser";

@Injectable()
export class CompanyRepository {
  constructor(
    private remoteGatewayFactory: RemoteGatewayFactory,
    private searchCompanyParser: SearchCompanyParser,
    private companyParser: CompanyParser,
    private addCompanyParser: AddCompanyParser,
    private contactParse: ContactParser,
    private addContactParser: AddContactParser,
    private searchCompanyFromMDMParser: SearchCompanyFromMDMParser,
    private userRoleMediator: UserRoleMediator,
    private companyFinancialReportsParser: CompanyFinancialReportsParser,
    private companyConfigurationParser: CompanyConfigurationParser,
    private exportFileAsXLSXParser: ExportFileAsXLSXParser,
  ) {}

  public async searchCompany(filterParams: ICompanyFilterParams, pagingRequest: PagingRequest): Promise<PageableResult> {
    const body = {
      StartIndex: pagingRequest.getStart(),
      TotalPerPages: pagingRequest.getEnd(),
      FilterText: filterParams.filterText ? filterParams.filterText.replace(/[./-]/g, "") : "",
      CompanyType: filterParams.companyType == null ? 2 : filterParams.companyType,
      Address: filterParams.address ? filterParams.address : "",
      Status: filterParams.status == null ? 3 : Number(filterParams.status),
    };
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/company/companies"), body, headers);
      return this.searchCompanyParser.parse(data);
    } catch (error) {
      console.error("Erro ao buscar companies", error);
      return await Promise.reject(error);
    }
  }

  public async findCompanyBy(fiscalNumber: string): Promise<Company> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/company/" + fiscalNumber), headers);
      return this.companyParser.parse(data);
    } catch (httpErrorResponse) {
      if (httpErrorResponse.error) {
        return await Promise.reject(httpErrorResponse.error.Data.ErrorMessage);
      } else if (httpErrorResponse.status == 404) {
        return await Promise.reject("Empresa não encontrada");
      }
    }
  }

  public async getCompanyRemainingLimit(fiscalNumber: string): Promise<CompanyRemainingLimit> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/company/balance/remaininglimit/" + fiscalNumber), headers);
      return this.companyParser.parseCompanyRemainingLimit(data);
    } catch (httpErrorResponse) {
      if (httpErrorResponse.error) {
        return await Promise.reject(httpErrorResponse.error.Data.ErrorMessage);
      } else if (httpErrorResponse.status == 404) {
        return await Promise.reject("Limite de empresa não encontrado");
      }
    }
  }

  public async getFinancialReportCompany(url: string): Promise<XLSXFileExportResult> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.downloadAsGetWithHeaders(url);
      return this.exportFileAsXLSXParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async findContactBy(fiscalNumber: string): Promise<CompanyContact[]> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/company/" + fiscalNumber + "/contact"), headers);
      return this.contactParse.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async addCompany(company: Company): Promise<any> {
    const body = JSON.stringify(company);

    const headers = { "Content-Type": "application/json" };

    try {
      const data = await this.remoteGatewayFactory.createDefaultRemoteGateway().postWithHeaders(new BackendUrl("/api/company"), body, headers);
      return this.addCompanyParser.parse(data);
    } catch (error) {
      return await Promise.reject(this.addCompanyParser.parse(error));
    }
  }

  public async addCompanyContact(companyContact: CompanyContact): Promise<any> {
    const body = JSON.stringify({
      CompanyContactKey: companyContact.getCompanyContactKey(),
      CompanyFiscalNumber: companyContact.getCompanyFiscalNumber(),
      Name: companyContact.getName(),
      Unit: companyContact.getUnit(),
      JobDescription: {
        Key: {
          Value: companyContact.getJobDescription().getKey(),
        },
        Title: companyContact.getJobDescription().getTitle(),
        IsGeneric: companyContact.getJobDescription().getIsGeneric(),
        Enabled: companyContact.getJobDescription().getEnabled(),
      },
      Telephone: companyContact.getTelephone(),
      Mobile: companyContact.getMobile(),
      Email: companyContact.getEmail(),
      Area: companyContact.getArea(),
      HasLegalRepresentativeInfo: companyContact.getHasLegalRepresentativeInfo(),
      AdditionalInformation: companyContact.getAdditionalInformation(),
      FederalRegistration: {
        Value: companyContact.getFederalRegistration(),
      },
      SocialNumber: {
        Value: companyContact.getSocialNumber(),
      },
    });
    const headers = {
      "Content-Type": "application/json",
    };
    try {
      const data = await this.remoteGatewayFactory.createDefaultRemoteGateway().postWithHeaders(new BackendUrl("/api/company/contact"), body, headers);
      return this.addContactParser.parse(data);
    } catch (error) {
      return await Promise.reject(this.addContactParser.parse(error));
    }
  }

  public async editCompanyContact(companyContact: CompanyContact): Promise<any> {
    const body = JSON.stringify({
      CompanyContactKey: companyContact.getCompanyContactKey(),
      CompanyFiscalNumber: companyContact.getCompanyFiscalNumber(),
      Name: companyContact.getName(),
      Unit: companyContact.getUnit(),
      JobDescription: {
        Key: {
          Value: companyContact.getJobDescription().getKey(),
        },
        Title: companyContact.getJobDescription().getTitle(),
        IsGeneric: companyContact.getJobDescription().getIsGeneric(),
        Enabled: companyContact.getJobDescription().getEnabled(),
      },
      Telephone: companyContact.getTelephone(),
      Mobile: companyContact.getMobile(),
      Email: companyContact.getEmail(),
      Area: companyContact.getArea(),
      HasLegalRepresentativeInfo: companyContact.getHasLegalRepresentativeInfo(),
      AdditionalInformation: companyContact.getAdditionalInformation(),
      FederalRegistration: {
        Value: companyContact.getFederalRegistration(),
      },
      SocialNumber: {
        Value: companyContact.getSocialNumber(),
      },
    });
    const headers = {
      "Content-Type": "application/json",
    };
    try {
      const data = await this.remoteGatewayFactory.createDefaultRemoteGateway().putWithHeaders(new BackendUrl("/api/company/contact"), body, headers);
      return this.addContactParser.parse(data);
    } catch (error) {
      return await Promise.reject(this.addContactParser.parse(error));
    }
  }

  public async deleteCompanyContact(companyContactKey: string) {
    const body = JSON.stringify(companyContactKey);
    const headers = {
      "Content-Type": "application/json",
    };
    try {
      const data = await this.remoteGatewayFactory.createDefaultRemoteGateway().delete(new BackendUrl("/api/company/contact/" + companyContactKey));
      return this.addContactParser.parse(data);
    } catch (error) {
      return await Promise.reject(this.addContactParser.parse(error));
    }
  }

  public async searchCompanyByFicalNumberFromMDM(fiscalNumber: FiscalNumber): Promise<CompanyMasterData> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/company/mdm/" + fiscalNumber.getValue()), headers);
      return this.searchCompanyFromMDMParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async findCompanyExportOrderKey(): Promise<ExportOrderKey> {
    const headers = {
      "Content-Type": "application/json",
    };

    try {
      const data = await this.remoteGatewayFactory.createDefaultRemoteGateway().getWithHeaders(new BackendUrl("/api/company/export/"), headers);
      const exportOrderKey = new ExportOrderKey(data.toString());
      return exportOrderKey;
    } catch (referenceError) {
      const errorMessage = referenceError.error.Data.ErrorMessage;
      return await Promise.reject(errorMessage);
    }
  }

  public async getCompanyFinancialReports(pagingRequest: PagingRequest): Promise<CompanyFinancialReportsPageableResult> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
    const body = JSON.stringify({
      filterText: pagingRequest.getOptions()[3].getValue(),
      StartAt: `${pagingRequest.getOptions()[0].getValue()} 00:00`,
      EndAt: `${pagingRequest.getOptions()[1].getValue()} 23:59`,
      StartIndex: pagingRequest.getStart(),
      TotalPerPages: pagingRequest.getEnd(),
      AllCompanies: false,
    });
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(new BackendUrl("/api/company/financial-reports-v2"), body, headers);
      return this.companyFinancialReportsParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async companyConfigurations(fiscalNumber: string): Promise<CompanyConfiguration> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/company/configurations/" + fiscalNumber), headers);

      return this.companyConfigurationParser.parse(data);
    } catch (httpErrorResponse) {
      if (httpErrorResponse.error) {
        return await Promise.reject(httpErrorResponse.error.Data.ErrorMessage);
      }
    }
  }

  public async updateCompanyConfigurations(fiscalNumber, transactionalLimit): Promise<CompanyConfiguration> {
    const body = JSON.stringify({
      FiscalNumber: fiscalNumber,
      TransactionalLimit: transactionalLimit,
    });
    const headers = {
      "Content-Type": "application/json",
    };
    try {
      const data = await this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/company/configurations/");

      if (body) {
        await data.putWithHeaders(backEndUrl, body, headers);
      }

      return this.companyConfigurationParser.parse(data);
    } catch (error) {
      return await Promise.reject(this.addContactParser.parse(error));
    }
  }

  public async createCompanyConfigurations(fiscalNumber, transactionalLimit): Promise<CompanyConfiguration> {
    const body = JSON.stringify({
      FiscalNumber: fiscalNumber,
      TransactionalLimit: transactionalLimit,
    });
    const headers = {
      "Content-Type": "application/json",
    };
    try {
      const data = await this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/company/configurations/");

      if (body) {
        await data.postWithHeaders(backEndUrl, body, headers);
      }
      return this.companyConfigurationParser.parse(data);
    } catch (error) {
      return await Promise.reject(this.addContactParser.parse(error));
    }
  }
}
